/*
 * The trace ("Xonix / Snake") path as data: pure functions, no React, no canvas.
 *
 * Points are in analysis-image pixels. A trace is a list of SEGMENTS -- one per
 * continuous movement (a held key, one mouse drag, one joystick push) -- so
 * Backspace can take back the last movement without losing the whole path.
 * Consecutive segments share their joining point.
 */
export function emptyTrace() {
  return { segments: [], cursor: null };
}

export function placeCursor(trace, point) {
  return { ...trace, cursor: point };
}

export function beginSegment(trace) {
  if (!trace.cursor) return trace;
  return { ...trace, segments: [...trace.segments, [trace.cursor]] };
}

const far = (a, b, min) => !a || Math.hypot(a[0] - b[0], a[1] - b[1]) >= min;

/** Append points to the open segment (opening one if needed); points closer than half a pixel are skipped. */
export function extendTrace(trace, points) {
  let current = trace.segments.length ? trace : beginSegment(trace);
  if (!current.segments.length) {
    if (!points.length) return trace;
    current = beginSegment(placeCursor(trace, points[0]));
  }
  const segments = current.segments.slice();
  const last = segments[segments.length - 1].slice();
  let cursor = current.cursor;
  points.forEach(p => {
    if (far(last[last.length - 1], p, 0.5)) {
      last.push(p);
      cursor = p;
    }
  });
  segments[segments.length - 1] = last;
  return { segments, cursor };
}

/** Close the open segment; a segment that never moved is dropped. */
export function endSegment(trace) {
  const segments = trace.segments.filter(s => s.length > 1);
  return segments.length === trace.segments.length ? trace : { ...trace, segments };
}

/** Take back the last movement: the cursor returns to where that movement began. */
export function undoSegment(trace) {
  if (!trace.segments.length) return trace;
  const removed = trace.segments[trace.segments.length - 1];
  return { segments: trace.segments.slice(0, -1), cursor: removed[0] };
}

export function tracePoints(trace) {
  const out = [];
  trace.segments.forEach((segment, index) => {
    segment.forEach((p, i) => {
      if (index > 0 && i === 0) return;
      out.push(p);
    });
  });
  return out;
}

export function polygonArea(points) {
  let area = 0;
  for (let i = 0, j = points.length - 1; i < points.length; j = i, i += 1) {
    area += (points[j][0] + points[i][0]) * (points[j][1] - points[i][1]);
  }
  return Math.abs(area / 2);
}

export function segmentIntersection(p1, p2, p3, p4) {
  const d = (p2[0] - p1[0]) * (p4[1] - p3[1]) - (p2[1] - p1[1]) * (p4[0] - p3[0]);
  if (Math.abs(d) < 1e-9) return null;
  const t = ((p3[0] - p1[0]) * (p4[1] - p3[1]) - (p3[1] - p1[1]) * (p4[0] - p3[0])) / d;
  const u = ((p3[0] - p1[0]) * (p2[1] - p1[1]) - (p3[1] - p1[1]) * (p2[0] - p1[0])) / d;
  if (t > 0 && t < 1 && u > 0 && u < 1) return [p1[0] + t * (p2[0] - p1[0]), p1[1] + t * (p2[1] - p1[1])];
  return null;
}

/**
 * Has the newest movement closed a loop? Either the head came within
 * `closeRadius` of an earlier point, or the newest step crossed an earlier step.
 * The last `ignoreLength` pixels of path are never candidates, so moving slowly
 * around a tight curve does not close on itself. Loops smaller than `minArea`
 * are jitter, not a shape.
 */
export function findClosure(points, { closeRadius = 8, ignoreLength = 30, minArea = 0 } = {}) {
  const n = points.length;
  if (n < 4) return null;
  const head = points[n - 1];
  let walked = 0;
  let limit = -1;
  for (let i = n - 1; i > 0; i -= 1) {
    walked += Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]);
    if (walked >= ignoreLength) { limit = i - 1; break; }
  }
  for (let i = 0; i <= limit; i += 1) {
    if (Math.hypot(points[i][0] - head[0], points[i][1] - head[1]) <= closeRadius) {
      const loop = points.slice(i);
      if (loop.length >= 3 && polygonArea(loop) >= minArea) return { start: i, loop };
    }
  }
  const a = points[n - 2];
  for (let i = 0; i < n - 3; i += 1) {
    const hit = segmentIntersection(points[i], points[i + 1], a, head);
    if (hit) {
      const loop = [hit, ...points.slice(i + 1, n - 1), hit];
      if (loop.length >= 4 && polygonArea(loop) >= minArea) return { start: i + 1, loop };
    }
  }
  return null;
}
